import React, { Fragment, memo } from 'react'
import PropTypes from 'prop-types'
import { Box, Grid, List } from '@material-ui/core'
import clsx from 'clsx'
import EditableHTML from '@v4/components/common/EditableHTML'
import ActionButton from '@v4/components/common/Buttons/ActionButton'
import SortHandler from '@v4/components/common/SortHandler'
import PageMetaData from '@v4/utils/data/PageMetaData'
import EditableContainer from '@v4/components/common/EditableContainer'
import Item from './components/Item'
import Timer from './components/Timer'
import { useCta18 } from './Cta18.hooks'

const Cta18 = props => {
	const [
		{
			items,
			classes,
			direction,
			date,
			timerColour,
			showCompletedContent,
			isButtonStateDisabled,

			buttonAction,
			buttonLink,
			buttonText,

			text,
			textConfig,
			textEditable,
			textKey,

			buttonTextEditable,
			buttonTextConfig,
			buttonTextKey,

			title,
			titleConfig,
			titleKey,
			titleEditable,

			additionalText,
			additionalTextConfig,
			additionalTextKey,
			additionalTextEditable,

			completedButtonText,
			completedButtonTextConfig,
			completedButtonTextKey,
			completedButtonTextEditable,
			completedButtonLink,
			completedButtonAction,

			completedAdditionalText,
			completedAdditionalTextConfig,
			completedAdditionalTextKey,
			completedAdditionalTextEditable,
		},
		{
			handleButtonClick,
			handleButtonTextChange,

			handleCompletedButtonClick,
			handleCompletedButtonTextChange,

			handleTextClick,
			handleTextChange,

			handleTitleClick,
			handleTitleChange,

			handleAdditionalTextClick,
			handleAdditionalTextChange,

			handleCompletedAdditionalTextClick,
			handleCompletedAdditionalTextChange,

			handleDateBlockClick,
		},
	] = useCta18(props)

	return (
		<Grid className="cta18-root" container justify="center" alignItems="center">
			<SortHandler direction={direction}>
				<Grid item xs={12} sm={12} lg={6} md={6} className="cta18-time-container">
					<Box className={classes.timeContainer}>
						<Timer date={date} timerColour={timerColour} onDateBlockClick={handleDateBlockClick} />
						{showCompletedContent ? (
							<Fragment key="completed">
								<EditableContainer
									visible={!!completedButtonText}
									fieldConfig={completedButtonTextConfig}
									onAdd={handleCompletedButtonTextChange}
									editable={completedButtonTextEditable}
									label="Button"
									wrapper={({ children }) => (
										<Box mt={5.5} className={classes.buttonWrap}>
											{children}
										</Box>
									)}
								>
									<ActionButton
										editable={completedButtonTextEditable}
										className={clsx(classes.button, 'cta18-completed-button')}
										action={completedButtonAction}
										href={completedButtonLink}
										onClick={handleCompletedButtonClick}
										onChange={handleCompletedButtonTextChange}
										key={completedButtonTextKey}
										disabled={isButtonStateDisabled}
									>
										{completedButtonText}
									</ActionButton>
								</EditableContainer>
								<EditableContainer
									visible={!!completedAdditionalText}
									fieldConfig={completedAdditionalTextConfig}
									onAdd={handleCompletedAdditionalTextChange}
									editable={completedAdditionalTextEditable}
								>
									<EditableHTML
										className={clsx(classes.text, 'cta18-additional-text')}
										html={completedAdditionalText}
										onClick={handleCompletedAdditionalTextClick}
										editable={completedAdditionalTextEditable}
										onChange={handleCompletedAdditionalTextChange}
										key={completedAdditionalTextKey}
									/>
								</EditableContainer>
							</Fragment>
						) : (
							<Fragment key="active">
								<EditableContainer
									visible={!!buttonText}
									fieldConfig={buttonTextConfig}
									onAdd={handleButtonTextChange}
									editable={buttonTextEditable}
									label="Button"
									wrapper={({ children }) => (
										<Box mt={5.5} className={classes.buttonWrap}>
											{children}
										</Box>
									)}
								>
									<ActionButton
										editable={buttonTextEditable}
										className={classes.button}
										action={buttonAction}
										href={buttonLink}
										onClick={handleButtonClick}
										onChange={handleButtonTextChange}
										key={buttonTextKey}
									>
										{buttonText}
									</ActionButton>
								</EditableContainer>
								<EditableContainer
									visible={!!additionalText}
									fieldConfig={additionalTextConfig}
									onAdd={handleAdditionalTextChange}
									editable={additionalTextEditable}
								>
									<EditableHTML
										className={clsx(classes.text, 'cta18-additional-text')}
										html={additionalText}
										onClick={handleAdditionalTextClick}
										editable={additionalTextEditable}
										onChange={handleAdditionalTextChange}
										key={additionalTextKey}
									/>
								</EditableContainer>
							</Fragment>
						)}
					</Box>
				</Grid>
				<Grid item lg={1} md={1} />
				<Grid item lg={5} md={5} sm={12} xs={12} className="cta18-content">
					<Box className={clsx(classes.leftContent)}>
						<EditableContainer
							visible={!!title}
							fieldConfig={titleConfig}
							onAdd={handleTitleChange}
							editable={titleEditable}
						>
							<EditableHTML
								className={clsx(classes.title, 'cta18-title')}
								html={title}
								onClick={handleTitleClick}
								editable={titleEditable}
								onChange={handleTitleChange}
								key={titleKey}
							/>
						</EditableContainer>

						<List className={clsx(classes.list, 'cta18-list')}>
							{_.map(items, (key, index) => (
								<Item key={key} itemKey={key} group="items" index={index} />
							))}
						</List>

						<EditableContainer
							visible={!!text}
							fieldConfig={textConfig}
							onAdd={handleTextChange}
							editable={textEditable}
						>
							<EditableHTML
								className={clsx(classes.text, 'cta18-text-block')}
								html={text}
								onClick={handleTextClick}
								editable={textEditable}
								onChange={handleTextChange}
								key={textKey}
							/>
						</EditableContainer>
					</Box>
				</Grid>
			</SortHandler>
		</Grid>
	)
}

Cta18.propTypes = {
	metaData: PropTypes.shape({
		title: PropTypes.instanceOf(PageMetaData),
		text: PropTypes.instanceOf(PageMetaData),
		additionalText: PropTypes.instanceOf(PageMetaData),
		image: PropTypes.instanceOf(PageMetaData),
		buttonText: PropTypes.instanceOf(PageMetaData),
		buttonLink: PropTypes.instanceOf(PageMetaData),
		buttonAction: PropTypes.instanceOf(PageMetaData),
		imagePosition: PropTypes.instanceOf(PageMetaData),
		countdownType: PropTypes.instanceOf(PageMetaData),
	}).isRequired,
}

export default memo(Cta18)
